import { environment } from '../../environments/environment';

/**
 * Branding helpers for the public post-wash feedback page.
 * SMS links look like: /{slug}/feedback?feedbackId=…&token=…
 * Display name / initials come from the path (or explicit `name` query) until the public API exists.
 */

/**
 * Same rules as svc-order `originFromPublicUrl`: http(s) origin only, else ''.
 * `https://carwashqa.way.com/api` → `https://carwashqa.way.com`
 */
export function feedbackPublicBaseUrlFromApiBase(apiBaseUrl: string): string {
  const trimmed = String(apiBaseUrl || '').trim();
  if (!trimmed || trimmed.startsWith('/') || trimmed.startsWith('\\')) {
    return '';
  }

  try {
    const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(withProtocol);
    if (!parsed.hostname || (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')) {
      return '';
    }
    return parsed.origin;
  } catch {
    return '';
  }
}

/** Origin of the Angular build currently running (dev/qa/staging/prod fileReplacement). */
export const FEEDBACK_PUBLIC_BASE_URL = feedbackPublicBaseUrlFromApiBase(environment.baseUrl);

/** Admin preview copy when no http(s) public origin can be derived. */
export const FEEDBACK_PUBLIC_ORIGIN_UNCONFIGURED = 'Origin not configured';

/**
 * Build the public feedback URL sent in post-wash SMS (same shape as svc-order).
 */
export function normalizeFeedbackPublicBaseUrl(baseUrl?: string): string {
  const resolved = baseUrl == null || baseUrl === '' ? FEEDBACK_PUBLIC_BASE_URL : baseUrl;
  return feedbackPublicBaseUrlFromApiBase(resolved);
}

export function buildFeedbackUrl(
  slug: string,
  token: string,
  feedbackId: string,
  baseUrl: string = FEEDBACK_PUBLIC_BASE_URL
): string {
  const base = normalizeFeedbackPublicBaseUrl(baseUrl);
  if (!base) {
    return '';
  }
  const pathSlug = normalizeFeedbackSlug(slug) || 'Location';
  return (
    `${base}/${encodeURIComponent(pathSlug)}/feedback` +
    `?feedbackId=${encodeURIComponent(feedbackId)}` +
    `&token=${encodeURIComponent(token)}`
  );
}

const MAX_FEEDBACK_SLUG_LENGTH = 100;

export interface FeedbackSlugPreviewLocation {
  id: string;
  branch?: string;
  feedback_slug?: string | null;
}

function normalizeFeedbackSlug(value: string): string {
  return String(value || '')
    .trim()
    .replace(/\s+/g, '');
}

function deriveFeedbackSlugBase(branch: string | undefined, locationId: string): string {
  const base = normalizeFeedbackSlug(branch || '');
  if (base) {
    return base;
  }

  return `location${String(locationId || '')
    .replace(/-/g, '')
    .slice(0, 8)}`;
}

function buildFeedbackSlugCandidate(base: string, suffix: number | ''): string {
  const suffixStr = suffix === '' ? '' : String(suffix);
  const maxBaseLen = MAX_FEEDBACK_SLUG_LENGTH - suffixStr.length;
  return `${base.slice(0, maxBaseLen)}${suffixStr}`;
}

/** Mirrors svc-admin-panel slug resolution using already-loaded location rows. */
export function resolveFeedbackSlugPreview(
  location: FeedbackSlugPreviewLocation,
  allLocations: FeedbackSlugPreviewLocation[]
): string {
  const existing = location.feedback_slug && String(location.feedback_slug).trim();
  if (existing) {
    return existing;
  }

  const rawBase = deriveFeedbackSlugBase(location.branch, location.id);
  const usedSlugs = new Set(
    allLocations
      .filter(row => row.id !== location.id)
      .map(row => row.feedback_slug && String(row.feedback_slug).trim())
      .filter((slug): slug is string => Boolean(slug))
  );

  let suffix: number | '' = '';
  let candidate = buildFeedbackSlugCandidate(rawBase, suffix);
  while (usedSlugs.has(candidate)) {
    suffix = suffix === '' ? 2 : suffix + 1;
    candidate = buildFeedbackSlugCandidate(rawBase, suffix);
  }

  return candidate;
}

/** Admin preview — vanity SMS path with readable placeholder tokens. */
export function buildFeedbackUrlPreview(
  slug: string,
  baseUrl: string = FEEDBACK_PUBLIC_BASE_URL
): string {
  const base = normalizeFeedbackPublicBaseUrl(baseUrl);
  if (!base) {
    return FEEDBACK_PUBLIC_ORIGIN_UNCONFIGURED;
  }
  const pathSlug = normalizeFeedbackSlug(slug) || 'Location';
  return `${base}/${encodeURIComponent(pathSlug)}/feedback?feedbackId={feedbackId}&token={token}`;
}

const SKIP_INITIAL_WORDS = new Set(['car', 'wash', 'the', 'a', 'an', 'and', 'of']);

/** Turn `AutoPride` / `auto-pride` into a display title (without forcing "Car Wash"). */
export function humanizeCarWashPath(pathSegment: string): string {
  const raw = decodeURIComponent(pathSegment || '')
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  if (!raw) {
    return '';
  }
  return raw.replace(/\b\w/g, char => char.toUpperCase());
}

/** Ensure the eyebrow reads like "Auto Pride Car Wash". */
export function formatCarWashDisplayName(nameOrPath: string): string {
  const titled = humanizeCarWashPath(nameOrPath);
  if (!titled) {
    return '';
  }
  if (/car\s*wash$/i.test(titled)) {
    return titled.replace(/\s+/g, ' ').trim();
  }
  return `${titled} Car Wash`;
}

/** Logo mark initials — e.g. "Auto Pride Car Wash" → "AP". */
export function initialsFromCarWashName(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(word => word && !SKIP_INITIAL_WORDS.has(word.toLowerCase()));
  const use = (words.length >= 2 ? words : name.trim().split(/\s+/)).slice(0, 2);
  const initials = use.map(word => word.charAt(0).toUpperCase()).join('');
  return initials.slice(0, 3) || 'CW';
}

/** True for `/feedback` and `/{carwash}/feedback` public SMS entry URLs. */
export function isFeedbackPublicPath(pathname: string): boolean {
  const path = pathname.split('?')[0].split('#')[0];
  if (path === '/feedback' || path.startsWith('/feedback/')) {
    return true;
  }
  return /^\/[^/]+\/feedback\/?$/.test(path);
}
